import React from 'react';
import { FaExclamationTriangle, FaTimes, FaInfoCircle, FaCheckCircle } from 'react-icons/fa';
import { AppError, ErrorType } from '../../types/error';
import { getErrorMessage } from '../../utils/errorUtils';

interface ErrorDisplayProps {
  error: AppError | Error | string | null;
  onDismiss?: () => void;
  className?: string;
  showIcon?: boolean;
  variant?: 'inline' | 'toast' | 'banner';
}

export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  onDismiss,
  className = '',
  showIcon = true,
  variant = 'inline',
}) => {
  if (!error) {
    return null;
  }

  const message = getErrorMessage(error);
  const isAppError = typeof error === 'object' && 'type' in error;
  const errorType = isAppError ? (error as AppError).type : ErrorType.UNKNOWN_ERROR;

  const getVariantClasses = () => {
    const baseClasses = 'flex items-center gap-3 p-3 rounded-lg text-sm';

    switch (variant) {
      case 'toast':
        return `${baseClasses} shadow-lg border fixed top-4 right-4 z-50 max-w-md`;
      case 'banner':
        return `${baseClasses} w-full border-l-4`;
      case 'inline':
      default:
        return `${baseClasses} border`;
    }
  };

  const getErrorTypeClasses = () => {
    switch (errorType) {
      case ErrorType.AUTH_FAILED:
      case ErrorType.UNAUTHORIZED:
      case ErrorType.PERMISSION_DENIED:
        return 'bg-red-50 text-red-800 border-red-200';
      case ErrorType.VALIDATION_ERROR:
      case ErrorType.REQUIRED_FIELD:
      case ErrorType.INVALID_FORMAT:
        return 'bg-yellow-50 text-yellow-800 border-yellow-200';
      case ErrorType.NETWORK_ERROR:
      case ErrorType.TIMEOUT_ERROR:
        return 'bg-orange-50 text-orange-800 border-orange-200';
      default:
        return 'bg-red-50 text-red-800 border-red-200';
    }
  };

  const getIcon = () => {
    switch (errorType) {
      case ErrorType.VALIDATION_ERROR:
      case ErrorType.REQUIRED_FIELD:
      case ErrorType.INVALID_FORMAT:
        return <FaInfoCircle className="text-yellow-600 flex-shrink-0" />;
      case ErrorType.NETWORK_ERROR:
      case ErrorType.TIMEOUT_ERROR:
        return <FaExclamationTriangle className="text-orange-600 flex-shrink-0" />;
      default:
        return <FaExclamationTriangle className="text-red-600 flex-shrink-0" />;
    }
  };

  return (
    <div className={`${getVariantClasses()} ${getErrorTypeClasses()} ${className}`}>
      {showIcon && getIcon()}
      <div className="flex-1">
        <p className="font-medium">{message}</p>
        {isAppError && (error as AppError).context?.operationContext && (
          <p className="text-xs opacity-75 mt-1">
            İşlem: {(error as AppError).context?.operationContext}
          </p>
        )}
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-current opacity-50 hover:opacity-75 flex-shrink-0"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
};

// Specialized components for different error types
export const ValidationError: React.FC<{ message: string; onDismiss?: () => void }> = ({
  message,
  onDismiss,
}) => (
  <ErrorDisplay
    error={{ type: ErrorType.VALIDATION_ERROR, message }}
    onDismiss={onDismiss}
    variant="inline"
  />
);

export const NetworkError: React.FC<{ onRetry?: () => void; onDismiss?: () => void }> = ({
  onRetry,
  onDismiss,
}) => (
  <div className="bg-orange-50 border border-orange-200 text-orange-800 p-4 rounded-lg">
    <div className="flex items-center gap-3">
      <FaExclamationTriangle className="text-orange-600 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-medium">Bağlantı Hatası</p>
        <p className="text-sm opacity-75">İnternet bağlantınızı kontrol edin ve tekrar deneyin.</p>
      </div>
    </div>
    <div className="flex gap-2 mt-3">
      {onRetry && (
        <button
          onClick={onRetry}
          className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700 transition"
        >
          Tekrar Dene
        </button>
      )}
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm hover:bg-gray-300 transition"
        >
          Kapat
        </button>
      )}
    </div>
  </div>
);

// Field validation error component
interface FieldErrorProps {
  error?: string;
  show?: boolean;
}

export const FieldError: React.FC<FieldErrorProps> = ({ error, show = true }) => {
  if (!error || !show) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 text-red-600 text-xs mt-1">
      <FaExclamationTriangle className="text-xs" />
      <span>{error}</span>
    </div>
  );
};

// Success message component for consistency
interface SuccessDisplayProps {
  message: string;
  onDismiss?: () => void;
  variant?: 'inline' | 'toast' | 'banner';
  className?: string;
}

export const SuccessDisplay: React.FC<SuccessDisplayProps> = ({
  message,
  onDismiss,
  variant = 'inline',
  className = '',
}) => {
  const getVariantClasses = () => {
    const baseClasses = 'flex items-center gap-3 p-3 rounded-lg text-sm';

    switch (variant) {
      case 'toast':
        return `${baseClasses} shadow-lg border fixed top-4 right-4 z-50 max-w-md`;
      case 'banner':
        return `${baseClasses} w-full border-l-4`;
      case 'inline':
      default:
        return `${baseClasses} border`;
    }
  };

  return (
    <div
      className={`${getVariantClasses()} bg-green-50 text-green-800 border-green-200 ${className}`}
    >
      <FaCheckCircle className="text-green-600 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-medium">{message}</p>
      </div>
      {onDismiss && (
        <button
          onClick={onDismiss}
          className="text-current opacity-50 hover:opacity-75 flex-shrink-0"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
};

// Loading error boundary component
interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error) => void;
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  { hasError: boolean; error: Error | null }
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.props.onError?.(error);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }

      return (
        <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <FaExclamationTriangle className="text-red-600 flex-shrink-0" />
            <div>
              <h3 className="font-medium">Bir hata oluştu</h3>
              <p className="text-sm opacity-75">Sayfa yüklenirken beklenmeyen bir hata oluştu.</p>
            </div>
          </div>
          <button
            onClick={this.resetError}
            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition"
          >
            Tekrar Dene
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
